import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import React from 'react'
import { message } from 'antd';
import psl from 'psl'
import _ from "lodash";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DashboardOutlined, CheckCircleOutlined, WarningOutlined, UserOutlined, LogoutOutlined, FileTextOutlined, ToolOutlined, SnippetsOutlined, CheckCircleFilled, ExclamationCircleFilled, CheckOutlined, CloseOutlined, LoadingOutlined, ShopOutlined, TeamOutlined} from '@ant-design/icons';
import { LINK_REGEX, LINK_SEPERATOR } from '../constants';

export async function downloadCSVFile(data, fileName='default_file.csv') {
  try {
    const csvData = convertToCSV(data);
    const blob = new Blob([csvData], { type: 'text/csv' });
    saveAs(blob, fileName); 
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

function convertToCSV(dataKeys) {
  const csvData = Papa.unparse(dataKeys, {
    header: true,
    delimiter: ',',
  });
  return csvData;
}

export const getIcon = (iconName, color) => {
  const AntIcon = {
    DashboardOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    UserOutlined,
    LogoutOutlined,
    FileTextOutlined,
    ToolOutlined,
    SnippetsOutlined,
    CheckOutlined,
    CloseOutlined,
    ExclamationCircleFilled,
    CheckCircleFilled,
    ShopOutlined,
    TeamOutlined
  }[iconName];
  return AntIcon ? <AntIcon style={{ color }} /> : null;
};

export const parseToListObj = (data) => {
  if (!data) return [];
  const parsedData = Papa.parse(data, { header: true }).data;
  const nonEmptyData = parsedData.filter(obj => Object.values(obj).some(value => value.trim() !== ''));
  return nonEmptyData;
};

export const createLinksMarkup = (text) => {
  const lines = text.split(LINK_SEPERATOR);

  let linkSectionIndex = 0; 
  return lines.map((line, index) => {
    const parts = line.split(LINK_REGEX);
    return (
      <div key={index}>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {part && LINK_REGEX.test(part) ? (
              <React.Fragment>
                {index > 0 && linkSectionIndex++ === 0 && ( <span><br/>{`Links:- `}<br/></span> ) }
                <a href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#008065' }}>
                  {part}
                </a>
                {index > 0 && ( <br/> )}
              </React.Fragment>
            ): <span>{part}</span>}
          </React.Fragment>
        ))}
      </div>
    );
  });
};

export const sortPlansAndOptions = (data, selectedKey) => {
  return data.map((plan) => {
    plan.options = _.sortBy(plan.options, [
      (option) => (option.key === selectedKey ? -1 : 1),
    ]);
    return plan;
  })
};

export const showSuccessMessage = (messageContent, duration = 2) => {
  message.success({
      className: "ProfileView_message",
      content: messageContent,
      icon: <CheckCircleFilled />,
      duration: duration,
  });
};

export const showErrorMessage = (messageContent, duration = 2) => {
  message.error({
      className: "ProfileView_message",
      content: messageContent,
      icon: <ExclamationCircleFilled />,
      duration: duration,
  });
};

export const showWarningMessage = (messageContent, duration = 2) => {
  message.warning({
      className: "ProfileView_message",
      content: messageContent,
      icon: <ExclamationCircleFilled />,
      duration: duration,
  });
};

export function showLoader(spinContent) {
  return (
    <div className='BulkUpload_loading'>
      <div className='custom-spin'>
        <LoadingOutlined />
        <span>{spinContent}</span>
      </div>
    </div>
  );
}



dayjs.extend(customParseFormat);

export const formatToCustomTime = (time) => {
  return dayjs(time).format('ddd, D MMM YY, h:mm A');
}

export function sanitizeNumericFractionInput(raw, maxDecimalPlaces = 2) {
  const stripped = String(raw).replace(/[^\d.]/g, '');
  const dot = stripped.indexOf('.');
  if (dot === -1) return stripped;
  const frac = stripped.slice(dot + 1).replace(/\./g, '').slice(0, maxDecimalPlaces);
  return stripped.slice(0, dot + 1) + frac;
}

export function parseDate(date) {
  if (!date) return null;
  if (dayjs.isDayjs(date)) return date;
  if (dayjs(date).isValid()) return dayjs(date);

  const formats = [
    "DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD",
    "MM-DD-YYYY", "MM/DD/YYYY", "YYYY-DD-MM", "YYYY/DD/MM",
    "DD MMM YYYY", "DD MMMM YYYY", "DD MMM YY", "DD MMMM YY",
    "MMM DD, YYYY", "MMMM DD, YYYY", "MMM DD YYYY", "MMMM DD YYYY",
    "MMM DD, YY", "MMMM DD, YY",
  ];

  for (const format of formats) {
    const parsed = dayjs(date, format, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
}

export function formatDisplayDate(date) {
  return date ? dayjs(date).format("DD/MM/YYYY") : '';
}

export function formatDisplayValue(value) {
  return value === 0 ? '' : value;
}

export  const getTextValue = (value, options) => {
  const matchedOption = options.find((opt) => opt.value === value);
  return matchedOption?.label ;
}

export  const getLabelFromValue = (value, options) => {
  const matchedOption = _.find(options, {value})
  return matchedOption?.label || value ;
};


export const checkEmptyFields = (formTemplateData, disabledFields = {}, disabledSections = {}) => {
  for (const fieldKey in formTemplateData) {
      const fieldData = formTemplateData[fieldKey];
      const fieldValue = fieldData['fieldValue'];
      const isFieldMandatory = fieldData.fieldStyle?.showMandatoryMarker;
      const isFieldDisabled = disabledFields[fieldKey] == undefined? fieldData.fieldStyle?.disabled : disabledFields[fieldKey] ;
      const isFieldHidden = disabledSections[fieldKey] == undefined? fieldData.fieldStyle?.hide : disabledSections[fieldKey] ;
      const isFieldFilled = fieldValue !== undefined && fieldValue.trim() !== '';
      if (isFieldMandatory && !isFieldFilled && !isFieldDisabled && !isFieldHidden) {
          return true; 
      }
  }

  return false; 
};

export const checkRegexInValidation = (formTemplateData, disabledFields = {}, disabledSections = {}) => {
  for (const fieldKey in formTemplateData) {
      const fieldData = formTemplateData[fieldKey];
      const fieldValue = fieldData['fieldValue'];
      const isFieldDisabled = disabledFields[fieldKey] == undefined? fieldData.fieldStyle?.disabled : disabledFields[fieldKey] ;
      const isFieldHidden = disabledSections[fieldKey] == undefined? fieldData.fieldStyle?.hide : disabledSections[fieldKey] ;
      if (fieldData.fieldValidator && fieldValue  && !isFieldDisabled && !isFieldHidden) {
          const regex = new RegExp(fieldData.fieldValidator);  
          if (!regex.test(fieldValue)) {
              return true;
          }
      }
  }
 return false;
};

export const formatCleanData = (data) => {
  if (data === undefined || data === null || data.trim() === '') {
    return ""
  }
  let cleanedData = data.trim();
  cleanedData = cleanedData.replace(/[\r\n]+/g, '').replace(/\s+/g, ' ');
  return cleanedData;
};


export const getStatusStyle = (status) => {
  switch (status) {
    case "pending":
      return { background: "#FAAD14" };
    case "active":
      return { background: "#52C41A" };
    case "inactive":
      return { background: "#FF4D4F" };
    case "completed":
      return { background: "#1890FF" };
    case "approved":
      return { background: "#52C41A" };
    case "rejected":
      return { background: "#FF4D4F" };
    case "draft":
      return { background: "#D9D9D9" };
    case "submitted":
      return { background: "#1890FF" };
    case "accepted":
      return { background: "#EB2F96" };
    case "negotiated":
      return { background: "#722ED1" };
    case "withdrawn":
      return { background: "#8C8C8C" };
    case "submitted":
      return { background: "#1890FF" };
    case "accepted":
      return { background: "#EB2F96" };
    case "NA":
      return { background: "#D9D9D9" };
    default:
      return { background: "#D9D9D9" };
  }
};

export const getStatusBadgeStyle = (status) => {
  switch (status) {
    case 'pending':
      return { label: 'Pending', color: '#D48806', border: '1px solid #FFE58F', background: '#FFFBE6' };
    case 'active':
      return { label: 'Active', color: '#389E0D', border: '1px solid #B7EB8F', background: '#F6FFED' };
    case 'completed':
      return { label: 'Completed', color: '#096DD9', border: '1px solid #91D5FF', background: '#E6F7FF' };
    case 'inactive':
      return { label: 'Inactive', color: '#CF1322', border: '1px solid #FFA39E', background: '#FFF1F0' };
    case 'cancelled':
      return { label: 'Cancelled', color: '#595959', border: '1px solid #D9D9D9', background: '#F5F5F5' };
  }
};

export const getStatusText = (status) => {
  switch (status) {
    case "submitted":
      return { color: "#1890FF" };
    case "pending":
      return { color: "#FAAD14" };
    case "rejected":
      return { color: "#FF4D4F" };
    case "accepted":
      return { color: "#EB2F96" };
    case "approved":
      return { color: "#52C41A" };
    case "negotiated":
      return { color: "#722ED1" };
    case "withdrawn":
      return { color: "#8C8C8C" };
    default:
      return {};
  }
};

export const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string') {
    return '';
  }
  return str?.charAt(0).toUpperCase() + str?.slice(1);
};

export const getYoutubeEmbedUrl = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  const id = (match && match[2].length === 11)
    ? match[2]
    : null;
  
  return id !== null ? `https://www.youtube.com/embed/${id}?autoplay=1&cc_load_policy=1&controls=0` : null;
}

export const getPathFromHostname = (keyName) => {
  if(!keyName) return '';
  return psl.parse(window.location.hostname)?.[keyName] || (keyName === 'subdomain' ? 'login' : '');
}

/**
 * Anonymizes an address by hiding specific details and showing only general location
 * @param {string} address - The full address string
 * @returns {string} - Anonymized address with masked prefix and visible city/state
 * 
 * Examples:
 * Input: "Apartment: name12, 2nd floor, HSR club road, Bengaluru, Karnataka 560102"
 * Output: "****, Bengaluru, Karnataka 560102"
 * 
 * Input: "123 Main Street, Bengaluru"
 * Output: "****, Bengaluru"
 * 
 * Input: "Bengaluru"
 * Output: "****, Bengaluru"
 */
export const anonymizeAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return '';
  }

  const parts = address.split(',').map(part => part.trim());
  
  if (parts.length === 1) {
    // Single part address - show masked prefix with the location
    return `****, ${parts[0]}`;
  }
  
  if (parts.length === 2) {
    // Two parts - mask first part, show second
    return `****, ${parts[1]}`;
  }

  // Keep only the last 2 parts visible (usually city and state/pincode)
  const visibleParts = parts.slice(-2);
  
  // Add masked indicator for hidden parts
  return `****, ${visibleParts.join(', ')}`;
};